import { getUserData } from '@/services/auth';
import { kickFirestoreReconnect } from '@/services/firestoreReconnect';
import { retryWithDelays } from '@/utils/retryWithDelays';

const PROFILE_RETRY_DELAYS_MS = [0, 700, 1600, 3200] as const;

export type UserProfileDoc = Record<string, unknown>;

export type LoadUserProfileResult =
  | { status: 'ok'; data: UserProfileDoc }
  | { status: 'missing' }
  | { status: 'unavailable' };

class ProfileRetryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProfileRetryError';
  }
}

/**
 * Loads `users/{uid}` with retries. Distinguishes a missing doc from a network miss.
 */
export async function loadUserProfileDoc(uid: string): Promise<LoadUserProfileResult> {
  if (!uid) return { status: 'unavailable' };
  try {
    return await retryWithDelays(
      async (): Promise<LoadUserProfileResult> => {
        const { userData, error, offline } = await getUserData(uid);
        if (userData && typeof userData === 'object') {
          return { status: 'ok', data: userData as UserProfileDoc };
        }
        if (error === 'User not found') {
          return { status: 'missing' };
        }
        throw new ProfileRetryError(offline ? 'profile_offline' : 'profile_unavailable');
      },
      PROFILE_RETRY_DELAYS_MS,
      {
        attemptTimeoutMs: 5000,
        beforeAttempt: async (attempt) => {
          if (attempt > 0) await kickFirestoreReconnect();
        },
      }
    );
  } catch {
    return { status: 'unavailable' };
  }
}
